import { State, Action, StateContext, Selector } from '@ngxs/store';
import { TokenService } from '../../../core/services/token.service';
import { AuthorizationService } from './service';
import { IAuthorizationState, IUser, ILoginData } from './types';
import { Populate, Login, Logout, SetAgreement, SetSession, UnsetSession } from './actions';
import { tap } from 'rxjs/operators';
import { Injectable } from '@angular/core';
import { StateResetAll } from 'ngxs-reset-plugin';
import { getRateType } from 'src/app/utils/helpers/helpers';
import * as Sentry from '@sentry/browser';

const defaults: IAuthorizationState = {
    user: null,
    role: null,
    agreement: null,
};

@State<IAuthorizationState>({
    name: 'authorization',
    defaults,
})
@Injectable()
export class AuthorizationState {
    @Selector()
    static user(state: IAuthorizationState) {
        return state.user;
    }

    @Selector()
    static role(state: IAuthorizationState) {
        return state.role;
    }

    @Selector()
    static agreement(state: IAuthorizationState) {
        return state.agreement;
    }

    @Selector()
    static currentCompany(state: IAuthorizationState) {
        if (state.user.company) {
            return state.user.company;
        } else if (state.user.subcontractor) {
            return state.user.subcontractor.company;
        } else if (state.user) {
            return {};
        }
    }

    @Selector()
    static rateType(state: IAuthorizationState) {
        return getRateType(state.user.subcontractor.company.rate_type);
    }

    @Selector()
    static subcontractorCompanyCurrency(state: IAuthorizationState) {
        return state.user.subcontractor.company.currency;
    }

    constructor(private tokenService: TokenService, private authorizationService: AuthorizationService) {}

    ngxsOnInit(ctx: StateContext<IAuthorizationState>): void {
        const token = this.tokenService.getSessionToken();
        if (!token) {
            ctx.dispatch(new Logout());
            return;
        }
        const tokenData = this.tokenService.getTokenData(token);
        if (!tokenData?.data?.role) {
            ctx.dispatch(new Logout());
            return;
        }
        ctx.patchState({ role: tokenData.data.role });
        ctx.dispatch(new Populate());
    }

    @Action(Populate)
    Populate(ctx: StateContext<IAuthorizationState>) {
        return this.authorizationService.populate().pipe(
            tap((user: IUser) => {
                ctx.patchState({ user });
                Sentry.setUser({ email: user.email });
            }),
        );
    }

    @Action(Login)
    Login(ctx: StateContext<IAuthorizationState>, action: Login) {
        return this.authorizationService.login(action.form).pipe(
            tap((data: ILoginData | { sms_sent: boolean }) => {
                if ('auth' in data && 'user' in data) {
                    // clear duplicated sessions
                    this.tokenService.getTokens().forEach((tokens) => {
                        if (this.tokenService.getTokenData(tokens.token).data.email === data.user.email) {
                            this.tokenService.deleteToken(tokens);
                        }
                    });

                    this.tokenService.addToken(data.auth);
                    this.tokenService.saveSessionToken(data.auth.token);
                    this.tokenService.saveSessionRefreshToken(data.auth.refresh_token);
                    ctx.patchState({
                        role: this.tokenService.getTokenData(data.auth.token).data.role,
                        user: data.user,
                    });
                }
            }),
        );
    }

    @Action(SetSession)
    SetSession(ctx: StateContext<IAuthorizationState>, action: SetSession) {
        this.tokenService.saveSessionToken(action.auth.token);
        this.tokenService.saveSessionRefreshToken(action.auth.refresh_token);

        ctx.patchState({
            role: this.tokenService.getTokenData(action.auth.token)?.data?.role,
        });
        ctx.dispatch(new Populate());
    }

    @Action(UnsetSession)
    UnsetSession(ctx: StateContext<IAuthorizationState>) {
        ctx.patchState({ user: null });
        this.tokenService.removeSessionToken();
        this.tokenService.removeSessionRefreshToken();
    }

    @Action(SetAgreement)
    SetAgreement(ctx: StateContext<IAuthorizationState>, action: SetAgreement) {
        ctx.patchState({ agreement: action.agreement });
    }

    @Action(Logout)
    Logout(ctx: StateContext<IAuthorizationState>) {
        const token = this.tokenService.getSessionToken();
        const refreshToken = this.tokenService.getSessionRefreshToken();
        if (token || refreshToken) {
            this.tokenService.deleteToken({ token, refresh_token: refreshToken });
        }
        this.tokenService.removeSessionToken();
        this.tokenService.removeSessionRefreshToken();
        Sentry.setUser(null);
        ctx.dispatch(new StateResetAll());
    }
}
